import { useQuery, useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import type {
  GetStudentApplication,
  PublicationData,
  AcademicRefereeData,
  StudentApplicationData,
} from './types';
import {
  GET_STUDENT_APPLICATION,
  GET_NAME_BY_NET_ID,
} from './GET_STUDENT_APPLICATION.gql';
import type {
  AcademicQualificationFormData,
  EmploymentFormData,
  ProfessionalQualificationFormData,
  TrainingFormData,
  ActivityFormData,
  MeritFormData,
  ProgrammeChoiceFormData,
  Mark,
  LanguageQualification,
} from '../../../Application/graphql';
import { checkInvalidDate } from '../../../Application/graphql';
import { getOptionsLabel } from '../../components';
import { PAGE_COMMON_TEXT } from '../../components/lang';
import {
  professionalFieldOptions,
  professionalQualificationOptions,
  employmentModeOptions,
  employmentFieldOptions,
  levelOfDegreeOptions,
  mediumOfInstructionOptions,
  mediumOfQualificationOptions,
  awardClassificationOptions,
  fieldOfStudyOptions,
  modeOfStudyOptions,
  PROGRAMME_TYPE,
  distinctiveScholarshipOptions,
  universityCategoryOptions,
} from '../../../Application/components';
import { PAGE_TEXT } from '../../../Application/components/ApplicationForm/lang';
import { type ResearchExperienceFormData } from '../../../Application/components/ApplicationForm/ResearchExperience/types';
import { type SelectOption } from '../../../../components';
import { formatDateString } from '../../../../utils';

const NA = PAGE_COMMON_TEXT.NA;

export const useGetStudentApplication = (id: string): GetStudentApplication => {
  const {
    data,
    loading: applicationLoading,
    error: applicationError,
  } = useQuery(GET_STUDENT_APPLICATION, {
    variables: { id },
    skip: !id,
    onCompleted: async data => {
      const proposedChiefSupervisor: string =
        data?.application?.programmeChoice?.proposedChiefSupervisor ?? '';
      const proposedChiefSupervisorBackup: string =
        data?.application?.programmeChoice?.proposedChiefSupervisorBackup ?? '';
      // to get the full name of proposedChiefSupervisor
      if (proposedChiefSupervisor) {
        await fetchAdvancedUser({ variables: { id: proposedChiefSupervisor } });
      }
      if (proposedChiefSupervisorBackup) {
        await fetchAdvancedUserBackup({
          variables: { id: proposedChiefSupervisorBackup },
        });
      }
    },
  });

  const [
    fetchAdvancedUser,
    { data: chiefSupervisorData, loading: userLoading, error: userError },
  ] = useLazyQuery(GET_NAME_BY_NET_ID);
  const [
    fetchAdvancedUserBackup,
    {
      data: supervisorBackUpData,
      loading: userBackupLoading,
      error: userBackupError,
    },
  ] = useLazyQuery(GET_NAME_BY_NET_ID);

  const countryList = data?.programmeChoiceMetadata?.countryList ?? [];
  const departmentOptions: SelectOption[] =
    data?.programmeChoiceMetadata?.departments ?? [];

  const rpgJoinPhdPartnerList =
    data?.programmeChoiceMetadata?.rpgJoinPhdPartnerList ?? [];
  const prgCollabPhdPartnerList =
    data?.programmeChoiceMetadata?.prgCollabPhdPartnerList ?? [];

  const {
    programmeChoice,
    academicQualifications = [],
    languageQualifications = [],
    otherTests,
    publications = [],
    merits = [],
    academicReferees = [],
    employments = [],
    professionalQualifications = [],
    activities = [],
    trainings = [],
    additionalDocuments = [],
    hkpfsInformation = {},
    distinctiveScholarships = [],
    declaration = {},
  } = data?.application ?? {};

  const academicQualificationsData: AcademicQualificationFormData[] =
    academicQualifications.map((academicQualification: any) => {
      const {
        presentIndicator,
        from,
        to,
        currentStudyYear = NA,
        studyMode = NA,
        institution,
        instructionMedium = NA,
        otherInstructionMedium = NA,
        studyField = NA,
        degreeLevel = NA,
        qualificationMeans = NA,
        programmeName = NA,
        qualificationStatus = NA,
        awardDate = NA,
        awardClassification = NA,
        officialMark = NA,
        outOfGpa = NA,
        marks = [],
        convertGPARuleId = '',
        markConversion = NA,
        expectUpdateDate = NA,
        withdrawDate = NA,
        academicOnlineVerificationCode = NA,
        degreeOnlineVerificationCode = NA,
        officialClassRanking = NA,
        outOfRank = NA,
        certificates = NA,
        transcripts = NA,
        markExplanations = NA,
        classRankProofs,
        otherDocuments,
        gpaIndicator,
        rankIndicator,
        academicCredentialIndicator,
        degreeCertIndicator,
      } = academicQualification ?? {};

      return {
        from: checkInvalidDate(from) ? NA : dayjs(from).format('DD/MM/YYYY'),
        to: (presentIndicator as boolean)
          ? 'present'
          : checkInvalidDate(to)
            ? NA
            : dayjs(to).format('DD/MM/YYYY'),
        // in case frontend pass *-1* as default val for graduated applicant
        // bcs currentStudyYear is required by API
        currentStudyYear: currentStudyYear === -1 ? NA : currentStudyYear,
        studyMode: getOptionsLabel(modeOfStudyOptions, studyMode),
        institutionName: institution?.name ?? NA,
        institutionCountry: getOptionsLabel(countryList, institution?.country),
        instructionMedium:
          instructionMedium === 'OTHERS'
            ? otherInstructionMedium
            : getOptionsLabel(mediumOfInstructionOptions, instructionMedium),
        studyField: getOptionsLabel(fieldOfStudyOptions, studyField),
        degreeLevel: getOptionsLabel(levelOfDegreeOptions, degreeLevel),
        qualificationMeans: getOptionsLabel(
          mediumOfQualificationOptions,
          qualificationMeans,
        ),
        programmeName,
        qualificationStatus,
        awardDate: checkInvalidDate(awardDate)
          ? NA
          : dayjs(awardDate).format('DD/MM/YYYY'),
        awardClassification: getOptionsLabel(
          awardClassificationOptions,
          awardClassification,
        ),
        officialMark,
        outOfGpa,
        marks: marks as Mark[],
        convertedMark: markConversion?.convertedMark ?? NA,
        convertGPARuleId,
        letterToGPARule: markConversion?.letterToGPARule?.name ?? NA,
        digitToLetterRule: markConversion?.digitToLetterRule?.name ?? NA,
        expectUpdateDate: checkInvalidDate(expectUpdateDate)
          ? NA
          : dayjs(expectUpdateDate).format('DD/MM/YYYY'),
        withdrawDate: checkInvalidDate(withdrawDate)
          ? NA
          : dayjs(withdrawDate).format('DD/MM/YYYY'),
        academicOnlineVerificationCode: (academicCredentialIndicator as boolean)
          ? academicOnlineVerificationCode
          : NA,
        degreeOnlineVerificationCode: (degreeCertIndicator as boolean)
          ? degreeOnlineVerificationCode
          : NA,
        officialClassRanking,
        outOfRank,
        certificates,
        transcripts,
        markExplanations,
        classRankProofs,
        otherDocuments,
        gpaIndicator,
        rankIndicator,
      };
    });

  const publicationData: PublicationData[] = publications.map(
    (publication: any) => {
      const {
        publicationType = NA,
        publicationAuthors = NA,
        publicationTitle = NA,
        publicationName = NA,
        applicantRole = NA,
        specificRole = NA,
        publicationStatus = NA,
        publicationDate,
        issuePageArticelNo = NA,
        peerReviewedIndicator = false,
        citationInfo = NA,
        publicationUrl,
        supportingDocuments,
      } = publication ?? {};

      return {
        publicationType,
        publicationAuthors,
        publicationTitle,
        publicationName,
        applicantRole,
        specificRole: (specificRole as boolean) ? specificRole : NA,
        publicationStatus,
        publicationDate: checkInvalidDate(publicationDate)
          ? NA
          : dayjs(publicationDate).format('DD/MM/YYYY'),
        issuePageArticelNo: (issuePageArticelNo as boolean)
          ? publicationUrl
          : NA,
        peerReviewedIndicator,
        citationInfo: (citationInfo as boolean) ? citationInfo : NA,
        publicationUrl: (publicationUrl as boolean) ? publicationUrl : NA,
        supportingDocuments,
      };
    },
  );

  const professionalQualificationsData: ProfessionalQualificationFormData[] =
    professionalQualifications.map((professionalQualification: any) => {
      const {
        awardDate,
        country,
        professionField,
        professionalQualification: professionalQualificationField = NA,
        awardBodyName,
        awardName = NA,
        attainmentMeans = NA,
        supportingDocuments,
      } = professionalQualification ?? {};

      const professionFieldLabel = getOptionsLabel(
        professionalFieldOptions,
        professionField,
      );
      const professionalQualificationLabel = getOptionsLabel(
        professionalQualificationOptions,
        professionalQualificationField,
      );

      return {
        awardDate: checkInvalidDate(awardDate)
          ? NA
          : dayjs(awardDate).format('DD/MM/YYYY'),
        country: getOptionsLabel(countryList, country),
        professionField: professionFieldLabel,
        professionalQualification: professionalQualificationLabel,
        awardBodyName,
        awardName: awardName ?? NA,
        attainmentMeans,
        supportingDocuments,
      };
    });

  const meritData: MeritFormData[] = merits.map((merit: any) => {
    const {
      meritName = NA,
      awardBodyName = NA,
      awardDate,
      country,
      attainmentMeans = NA,
      supportingDocuments,
    } = merit ?? {};

    return {
      meritName,
      awardBodyName,
      awardDate: checkInvalidDate(awardDate)
        ? NA
        : dayjs(awardDate).format('DD/MM/YYYY'),
      country: getOptionsLabel(countryList, country),
      attainmentMeans,
      supportingDocuments,
    };
  });

  const academicRefereesData: AcademicRefereeData[] = academicReferees.map(
    (academicReferee: any) => {
      const {
        title = NA,
        name,
        institution = NA,
        position = NA,
        relationship = NA,
        email = NA,
        referenceReport,
      } = academicReferee ?? {};

      return {
        title,
        name,
        institution,
        position,
        relationship,
        email,
        referenceReport: {
          id: referenceReport?.id ?? '',
          name: referenceReport?.name ?? '',
          link: referenceReport?.link ?? '',
        },
      };
    },
  );

  const employmentsData: EmploymentFormData[] = employments.map(
    (employment: any) => {
      const {
        presentEmploymentIndicator,
        from,
        to,
        employmentMode,
        employmentField,
        organisationName = NA,
        position = NA,
        majorResponsibilities = NA,
        supportingDocuments,
      } = employment ?? {};

      const employmentModeLabel = getOptionsLabel(
        employmentModeOptions,
        employmentMode,
      );
      const employmentFieldLabel = getOptionsLabel(
        employmentFieldOptions,
        employmentField,
      );

      return {
        from: formatDateString(from, false) ?? NA,
        to: (presentEmploymentIndicator as boolean)
          ? 'present'
          : (formatDateString(to, false) ?? NA),
        employmentMode: employmentModeLabel,
        employmentField: employmentFieldLabel,
        organisationName,
        position,
        majorResponsibilities,
        supportingDocuments,
      };
    },
  );

  const activitiesData: ActivityFormData[] = activities.map((activity: any) => {
    const {
      presentActivityIndicator,
      from,
      to,
      activityType = NA,
      organisationName = NA,
      position = NA,
      majorResponsibilities = NA,
      supportingDocuments,
    } = activity ?? {};

    return {
      from: formatDateString(from, false) ?? NA,
      to: (presentActivityIndicator as boolean)
        ? 'present'
        : (formatDateString(to, false) ?? NA),
      activityType,
      organisationName,
      position,
      majorResponsibilities,
      supportingDocuments,
    };
  });

  const trainingsData: TrainingFormData[] = trainings.map((training: any) => {
    const {
      from,
      to,
      presentTrainingIndicator,
      country,
      organisationName,
      programmeName,
      supportingDocuments,
    } = training ?? {};
    return {
      from: formatDateString(from, false) ?? NA,
      to: (presentTrainingIndicator as boolean)
        ? 'present'
        : (formatDateString(to, false) ?? NA),
      country: getOptionsLabel(countryList, country),
      organisationName,
      programmeName,
      supportingDocuments,
    };
  });

  const distinctiveScholarshipsData = (distinctiveScholarships ?? []).map(
    (distinctiveScholarship: any) => {
      const { fundingSource, reason, files } = distinctiveScholarship ?? {};
      return {
        fundingSource: getOptionsLabel(
          distinctiveScholarshipOptions,
          fundingSource,
        ),
        reason,
        files,
      };
    },
  );

  const {
    entryYear = '',
    studyMode = '',
    department = '',
    programme = '',
    programmeType = '',
    partner,
    researchArea = '',
    researchAreaBackup = PAGE_TEXT.PROGRAMME_CHOICE.OPTIONS.NO_INDICATION,
    proposedChiefSupervisor,
    acceptNoResearchIndicator,
    proposedChiefSupervisorBackup,
  } = programmeChoice ?? {};

  const {
    university = '',
    proposedChiefSupervisor: proposedChiefSupervisorPartner = '',
    universityCategory = '',
    isPolyUHomeUniversity = false,
    physicalLocation = '',
    partnerUniversityApplicationNo = '',
  } = partner ?? {};

  const programmeChoiceFormData: ProgrammeChoiceFormData = {
    entryYear,
    studyMode,
    department: getOptionsLabel(departmentOptions, department),
    programme,
    programmeType,
    isDualOrCollaborative:
      programmeType === PROGRAMME_TYPE.COLLABORATIVE_PHD_DEGREE_PROGRAMME ||
      programmeType === PROGRAMME_TYPE.DUAL_PHD_DEGREE_PROGRAMME,
    partner: {
      university:
        programmeType === PROGRAMME_TYPE.DUAL_PHD_DEGREE_PROGRAMME
          ? getOptionsLabel(rpgJoinPhdPartnerList, university)
          : getOptionsLabel(prgCollabPhdPartnerList, university),
      proposedChiefSupervisor: proposedChiefSupervisorPartner,
      universityCategory: getOptionsLabel(
        universityCategoryOptions,
        universityCategory,
      ),
      isPolyUHomeUniversity: isPolyUHomeUniversity ?? false,
      physicalLocation,
      partnerUniversityApplicationNo,
    },
    researchArea,
    proposedChiefSupervisor:
      !(proposedChiefSupervisor as boolean) && !!id && (department as boolean) // Check if applicant has programme choice and choose proposedChiefSupervisor
        ? PAGE_TEXT.PROGRAMME_CHOICE.OPTIONS.NO_INDICATION
        : `${chiefSupervisorData?.advancedUser?.surname} ${chiefSupervisorData?.advancedUser?.givenName}`,
    researchAreaBackup,
    proposedChiefSupervisorBackup:
      !(proposedChiefSupervisorBackup as boolean) &&
      !!id &&
      (department as boolean)
        ? PAGE_TEXT.PROGRAMME_CHOICE.OPTIONS.NO_INDICATION
        : `${supervisorBackUpData?.advancedUser?.surname} ${supervisorBackUpData?.advancedUser?.givenName}`,

    acceptNoResearchIndicator: acceptNoResearchIndicator ?? false,
  };
  const researchExperienceData = data?.application?.researchExperiences?.[0];
  const researchExperience: ResearchExperienceFormData = {
    pastResearchExperience:
      researchExperienceData?.pastResearchExperience ?? '',
    proposedResearchTopic: researchExperienceData?.proposedResearchTopic ?? '',
    proposedResearchPlan: researchExperienceData?.proposedResearchPlan ?? '',
    reasons: researchExperienceData?.reasons ?? '',
    longTermCareerPlan: researchExperienceData?.longTermCareerPlan ?? '',
    expectedContribution: researchExperienceData?.expectedContribution ?? '',
    others: researchExperienceData?.others ?? '',
    researchProposal: researchExperienceData?.researchProposal ?? '',
  };

  const languageQualificationData = languageQualifications?.[0] ?? {};
  const languageQualification: LanguageQualification = {
    needEnglishTest: languageQualificationData?.needEnglishTest ?? false,
    testType: languageQualificationData?.testType ?? '',
    testDate: checkInvalidDate(languageQualificationData?.testDate)
      ? NA
      : dayjs(languageQualificationData?.testDate).format('DD/MM/YYYY'),
    resultAnnouncedIndicator:
      languageQualificationData?.resultAnnouncedIndicator ?? false,
    expectedResultUpdateDate: checkInvalidDate(
      languageQualificationData?.expectedResultUpdateDate,
    )
      ? NA
      : dayjs(languageQualificationData?.expectedResultUpdateDate).format(
          'DD/MM/YYYY',
        ),
    result: {
      resultReceiptDate: checkInvalidDate(
        languageQualificationData?.result?.resultReceiptDate,
      )
        ? NA
        : dayjs(languageQualificationData?.result?.resultReceiptDate).format(
            'DD/MM/YYYY',
          ),
      listening: languageQualificationData?.result?.listening,
      writing: languageQualificationData?.result?.writing,
      speaking: languageQualificationData?.result?.speaking,
      reading: languageQualificationData?.result?.reading,
      overall: languageQualificationData?.result?.overall,
    },
    referenceNo: languageQualificationData?.referenceNo,
    supportingDocuments: languageQualificationData?.supportingDocuments,
  };
  const studentApplication: StudentApplicationData = {
    programmeChoice: programmeChoiceFormData,
    academicQualifications: academicQualificationsData,
    languageQualification,
    otherTest: otherTests?.[0],
    publications: publicationData,
    merits: meritData,
    academicReferees: academicRefereesData,
    employments: employmentsData,
    professionalQualifications: professionalQualificationsData,
    trainings: trainingsData,
    activities: activitiesData,
    additionalDocuments,
    hkpfsInformation: {
      HKPFSReferenceNumber: hkpfsInformation?.HKPFSReferenceNumber ?? '-',
    },
    researchExperience,
    distinctiveScholarships: distinctiveScholarshipsData,
    applicationDeclaration: declaration,
  };

  return {
    studentApplication,
    loading: applicationLoading || userLoading || userBackupLoading,
    error: applicationError ?? userError ?? userBackupError,
  };
};
